import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import { Clock, Search } from 'lucide-react';
import {
  ICampaign, ISenderIdentity, CampaignLeadSource, ILeadPreferences, TERMINAL_LEAD_STATUSES, sumEnrolledLeads,
} from '../../../../interfaces/models/campaign';
import { campaignMobile } from '../../utils/campaignBreakpoints';

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SENDERS_PREVIEW = 5;

const SOURCE_LABELS: Record<CampaignLeadSource, string> = {
  leadsearch: 'LinkedIn search',
  contacts: 'LeadDelta contacts',
  csv: 'CSV import',
  posts: 'LinkedIn post engagers',
  sn_lists: 'Sales Navigator list',
};

// Same order as the design's example: industry - title - country - size, then the rest.
const PREFERENCE_ORDER: (keyof ILeadPreferences)[] = [
  'companyIndustry', 'jobTitle', 'countryCode', 'companySize', 'state', 'city', 'school', 'companyName',
];

function leadSourceText(campaign: ICampaign): string {
  const source = campaign.leadSource;
  if (!source || source === 'empty') return 'No leads source selected';
  const label = SOURCE_LABELS[source] ?? source;
  if (source !== 'leadsearch' || !campaign.leadPreferences) return label;
  const filters = PREFERENCE_ORDER
    .map(key => (campaign.leadPreferences?.[key] ?? []).join(', '))
    .filter(Boolean);
  return filters.length ? `${label}: ${filters.join(' - ')}` : label;
}

const pad = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

interface ICampaignDetailsProps {
  campaign: ICampaign;
  senders: ISenderIdentity[];
}

const CampaignDetails: FunctionComponent<ICampaignDetailsProps> = ({ campaign, senders }) => {
  const [showAllSenders, setShowAllSenders] = useState(false);

  const total = sumEnrolledLeads(campaign.statusCounts);
  const finished = TERMINAL_LEAD_STATUSES.reduce((sum, s) => sum + (campaign.statusCounts?.[s] ?? 0), 0);
  const progress = total ? Math.round((finished / total) * 100) : 0;

  const schedule = campaign.sendingWindow;
  // Without a schedule the campaign may send any day, at any hour.
  const scheduled = !!schedule?.enabled;
  const activeDays = scheduled ? new Set(schedule?.days ?? []) : new Set(WEEK_DAYS);

  const senderNames = senders.map(s => s.name || s.linkedin?.name || s.mail?.fromName || 'Unnamed sender');
  const visibleSenders = showAllSenders ? senderNames : senderNames.slice(0, SENDERS_PREVIEW);

  return (
    <Panel>
      <PanelTitle>Campaign details</PanelTitle>
      <Grid>
        <Card $span={3}>
          <CardTitle>Campaign progress</CardTitle>
          <ProgressRow title={`${finished} of ${total} leads finished`}>
            <ProgressTrack><ProgressFill style={{ width: `${progress}%` }} /></ProgressTrack>
            <ProgressValue>{progress}%</ProgressValue>
          </ProgressRow>
        </Card>

        <Card $span={3}>
          <CardTitle>Active days</CardTitle>
          <DayRow>
            {WEEK_DAYS.map(day => <DayChip key={day} $active={activeDays.has(day)}>{day}</DayChip>)}
          </DayRow>
        </Card>

        <Card $span={2}>
          <CardTitle>Active hours</CardTitle>
          <InfoRow>
            <Clock size={18} strokeWidth={1.5} />
            <span>
              {scheduled && schedule
                ? <>{pad(schedule.startHour)} - {pad(schedule.endHour)} <Divider>|</Divider> {schedule.timezone.replace(/_/g, ' ')}</>
                : 'Any time — no sending schedule'}
            </span>
          </InfoRow>
        </Card>

        <Card $span={2}>
          <CardTitle>Leads source</CardTitle>
          <InfoRow>
            <Search size={18} strokeWidth={1.5} />
            <span>{leadSourceText(campaign)}</span>
          </InfoRow>
        </Card>

        <Card $span={2}>
          <CardHeader>
            <CardTitleRow>
              <CardTitle>Campaign senders</CardTitle>
              <CountChip>{senderNames.length}</CountChip>
            </CardTitleRow>
            {senderNames.length > SENDERS_PREVIEW && (
              <SeeAll type="button" onClick={() => setShowAllSenders(v => !v)}>
                {showAllSenders ? 'Show less' : 'See all'}
              </SeeAll>
            )}
          </CardHeader>
          {senderNames.length ? (
            <SenderChips>
              {visibleSenders.map((name, i) => <SenderChip key={`${name}-${i}`}>{name}</SenderChip>)}
            </SenderChips>
          ) : (
            <Muted>No senders selected</Muted>
          )}
        </Card>
      </Grid>
    </Panel>
  );
};

export default CampaignDetails;

const Panel = styled.div`
  background: #FFFFFF;
  border: 1px solid #E9EAEB; border-radius: 12px;
  padding: 24px;
  display: flex; flex-direction: column; gap: 20px;
  ${campaignMobile`padding: 16px;`}
`;

const PanelTitle = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 20px; font-weight: 700; line-height: 30px;
  color: #111927;
`;

// Six columns: progress and days take half a row each, the three short cards a third.
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 12px;
  ${campaignMobile`grid-template-columns: 1fr;`}
`;

const Card = styled.div<{ $span: number }>`
  grid-column: span ${({ $span }) => $span};
  display: flex; flex-direction: column; gap: 16px;
  padding: 20px; min-width: 0;
  border: 1px solid #E5E7EB; border-radius: 12px;
  ${campaignMobile`grid-column: auto;`}
`;

const CardHeader = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px;`;

const CardTitleRow = styled.div`display: flex; align-items: center; gap: 8px;`;

const CardTitle = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; font-weight: 600; line-height: 20px;
  color: #252B37;
`;

const CountChip = styled.span`
  min-width: 22px; padding: 0 6px; border: 1px solid #E5E7EB; border-radius: 6px;
  text-align: center; font-family: NunitoSans; font-size: 12px; line-height: 20px; color: #6B7280;
`;

const ProgressRow = styled.div`display: flex; align-items: center; gap: 12px;`;

const ProgressTrack = styled.div`
  flex: 1; height: 4px; border-radius: 4px; background: #EEF2FF; overflow: hidden;
`;

const ProgressFill = styled.div`height: 100%; border-radius: 4px; background: #454DE5;`;

const ProgressValue = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; color: #252B37;
`;

const DayRow = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;

const DayChip = styled.span<{ $active: boolean }>`
  min-width: 48px; height: 32px; padding: 0 12px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 100px;
  border: 1px solid ${({ $active }) => ($active ? '#6061F8' : '#E5E7EB')};
  background: ${({ $active }) => ($active ? '#6061F8' : '#FFFFFF')};
  color: ${({ $active }) => ($active ? '#FFFFFF' : '#6B7280')};
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 12px; font-weight: ${({ $active }) => ($active ? 600 : 400)};
`;

const InfoRow = styled.div`
  display: flex; align-items: center; gap: 10px; min-width: 0;
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; line-height: 20px; color: #111927;
  svg { flex-shrink: 0; color: #8C96AD; }
  span { min-width: 0; overflow-wrap: anywhere; }
`;

const Divider = styled.span`margin: 0 4px; color: #111927;`;

const SeeAll = styled.button`
  border: none; background: transparent; padding: 0;
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; font-weight: 600;
  color: #454DE5; cursor: pointer;
`;

const SenderChips = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;

const SenderChip = styled.span`
  padding: 2px 6px; border-radius: 4px; background: #F3F4F6;
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; line-height: 20px; color: #111927;
`;

const Muted = styled.span`
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; color: #6B7280;
`;
